import { MouseEvent, useEffect, useState } from 'react';
import { PATHS, navigate } from '@client/routes';
import { Box } from '@mui/material';
import { Post, ReactionType } from '../../../../generated/graphql';
import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import { noop } from 'lodash';
import { SensitiveContent } from './SensitiveContent';
import { mixTrack } from '@services/mixpanel';

type CommunityPostCellProps = {
  influencerId: number;
  communityId: number;
  isUserCommunityOwner: boolean;
  postData: Post;
  onAddComment?: () => void;
  onDelete?: () => void;
  onBlock?: (communityId: number, userId: number) => void;
  onToggleReaction?: (reactionType: ReactionType) => void;
};

export const CommunityPostCell = ({
  influencerId,
  communityId,
  isUserCommunityOwner,
  postData,
  onAddComment = undefined,
  onDelete = noop,
  onBlock = noop,
  onToggleReaction,
}: CommunityPostCellProps) => {
  const [hideSensitiveContent, setHideSensitiveContent] = useState<boolean>(
    postData.isSensitiveContent,
  );
  const handleOnAddComment = () => {
    mixTrack({
      type: 'Comment Added',
      payload: {
        'Post Type':
          postData.author.id === influencerId ? 'Creator' : 'Consumer',
        'Media Type': postData.media
          .map((m) => (m.video ? 'video' : 'image'))
          .join(', '),
      },
    });

    if (onAddComment) {
      onAddComment();
    } else {
      goToCommunityComments();
    }
  };

  const goToCommunityComments = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    navigate(PATHS.COMMUNITY_POST(influencerId, communityId, postData.id));
  };

  const handleHideSensitiveContent = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setHideSensitiveContent(false);
  };

  useEffect(() => {
    mixTrack({
      type: 'Post Viewed',
      payload: {
        'Has Comments': String(
          postData.reactions?.some((reaction) => reaction.type === 'Comment'),
        ),
        'Has Reactions': String(postData.reactions?.length > 0),
        'Media Count': postData.media.length,
        'Number of comments':
          postData.reactions?.filter((reaction) => reaction.type === 'Comment')
            .length || 0,
        'Number of reactions': postData.reactions?.length || 0,
        'Post Type':
          postData.author.id === influencerId ? 'Creator' : 'Consumer',
        'Media Type': postData.media
          .map((m) => (m.video ? 'video' : 'image'))
          .join(', '),
        Event: '?', // FIXME: should be removed
      },
    });
  }, []);

  return (
    <Box
      className="flex flex-col justify-start gap-4 p-5 border-t first:border-0"
      onClick={goToCommunityComments}
    >
      <PostHeader
        communityId={communityId}
        author={postData.author}
        createdAt={postData.createdAt}
        id={postData.id}
        isUserCommunityOwner={isUserCommunityOwner}
        onDelete={onDelete}
        onBlock={onBlock}
      />
      {hideSensitiveContent ? (
        <SensitiveContent
          isPostCell={true}
          handleHideSensitiveContent={handleHideSensitiveContent}
        />
      ) : (
        <>
          <PostContent text={postData.text} media={postData.media} />
          <PostReactions
            reactions={postData.reactions || []}
            onToggleReaction={onToggleReaction}
          />
          <PostFooter
            reactions={postData.reactions || []}
            onAddComment={handleOnAddComment}
          />
        </>
      )}
    </Box>
  );
};
